import { Button } from '@mui/material';
import React, { FC, useEffect, useRef, useState } from 'react';
import ReactToPrint, { useReactToPrint } from 'react-to-print';
import { SvgAssets } from '../../common/models/svg';
import { Building, Project, Site } from '../../confi-state';
import { isProdOrDevEnv } from '../../environment.enum';
import { SectionJSON } from '../../models/section-json';
import { ServiceManager } from '../../services/service-manager';
import DocumentationPagePrint from '../DocumentationPagePrint/DocumentationPagePrint';
import styles from './DocumentationPage.module.css';
import { ContentNode } from 'react-to-print/lib/types/ContentNode';

interface DocumentationPageProps {
    currentProject: Project,
    currentSite: Site,
    buildings: Building[],
    section: SectionJSON,
    svgAssets: SvgAssets,
    onChangeSvgAssets: (svgAssets: SvgAssets) => void,
    addSpinner: (id: string, message: string) => void,
    removeSpinner: (id: string) => void,
}

const DocumentationPage: FC<DocumentationPageProps> = ({
    currentProject,
    currentSite,
    buildings,
    section,
    svgAssets,
    onChangeSvgAssets,
    addSpinner,
    removeSpinner
}) => {

    const [notDeprecatedBuildings, setNotDeprecatedBuildings] = useState<Building[]>([]);
    const [perspectiveImageSite1, setPerspectiveImageSite1] = useState<string>(undefined);
    const [perspectiveImageSite2, setPerspectiveImageSite2] = useState<string>(undefined)

    const [topImageSite, setTopImageSite] = useState<string>(undefined)

    const [buildingImages, setBuildingImages] = useState<{ perspective1: string, perspective2: string, top: string }[]>([]);
    const firstRenderRef = useRef(true);

    const componentRef = useRef(undefined);

    const takeScreenShots = async () => {
        const spinId = 'documentation-pages';
        addSpinner(spinId, 'Documentatie wordt gegenereerd...');
        const perpsectiveImageSite1 = await ServiceManager.sceneManager.takePerspectiveScreenshot([-700, 500, 0], [-60, 0, 0], 0.4, 2000, 1000);
        setPerspectiveImageSite1(perpsectiveImageSite1);

        const perpsectiveImageSite2 = await ServiceManager.sceneManager.takePerspectiveScreenshot([0, 500, -700], [0, 0, -60], 0.4, 1000, 450);
        setPerspectiveImageSite2(perpsectiveImageSite2);

        const top = await ServiceManager.sceneManager.takeTopScreenshot(500, 500);
        setTopImageSite(top);

        if (buildings.length > 0) {
            const all = [];

            for (const b of buildings) {
                if (!b.deprecated) {
                    const persp1 = await ServiceManager.sceneManager.takeScreenshotGroupId(
                        b.id as string, 2000, 1000, b.props.buildingLength, b.props.buildingHeight, b.locationInSite.rotation[1], true
                    );
                    const persp2 = await ServiceManager.sceneManager.takeScreenshotGroupId(
                        b.id as string, 1500, 700, b.props.buildingLength, b.props.buildingHeight, b.locationInSite.rotation[1], false
                    );
                    const top = await ServiceManager.sceneManager.takeScreenshotTopGroupId(
                        b.id as string, 800, 800, b.props.buildingLength, b.props.buildingHeight, b.locationInSite.rotation[1]
                    );

                    all.push([persp1, persp2, top]);
                }
            }

            let res = all;
            const buildingImagesState: { perspective1: string, perspective2: string, top: string }[] = [];
            res.forEach(r => {
                if (r && r.length === 3) {
                    buildingImagesState.push({
                        perspective1: r[0],
                        perspective2: r[1],
                        top: r[2],
                    })
                }
            })
            ServiceManager.sceneManager.restoreDefaultCamera();
            setBuildingImages(buildingImagesState);
            removeSpinner(spinId);
        }
    }

    useEffect(() => {
        if (!isProdOrDevEnv() && firstRenderRef.current) {
            firstRenderRef.current = false;
            return;
        }
        if (notDeprecatedBuildings && notDeprecatedBuildings.length > 0) {
            takeScreenShots();
        }
    }, [notDeprecatedBuildings])

    useEffect(() => {
        if (!isProdOrDevEnv() && firstRenderRef.current) {
            firstRenderRef.current = false;
            return;
        }
        setNotDeprecatedBuildings(buildings.filter(b => !b.deprecated));
    }, [])

    const handlePrint = useReactToPrint({
        contentRef: componentRef as React.RefObject<ContentNode>,
        documentTitle: 'Project documentatie',
    });

    return (
        <div className={styles.DocumentationPage} data-testid="DocumentationPage">
            <div className={styles.context}>
                <DocumentationPagePrint currentProject={currentProject} currentSite={currentSite} buildings={notDeprecatedBuildings} siteImagePerspective1={perspectiveImageSite1} siteImagePerspective2={perspectiveImageSite2} buildingImages={buildingImages} siteImageTop={topImageSite} ref={componentRef} />
                <br></br>
                <Button className={styles.printButton} color="primary" variant="contained" onClick={() => handlePrint()}>Printen</Button>
            </div>
        </div>
    )
};

export default DocumentationPage;
